import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';

const OrderConfirmationPage = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const data = await api.getOrder(orderId);
        setOrder(data);
      } catch (error) {
        console.error('Error fetching order:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  if (loading) {
    return (
      <div className="paper-texture min-h-screen flex items-center justify-center">
        <div className="inline-block w-12 h-12 border-4 border-cape-red border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="paper-texture min-h-screen flex flex-col items-center justify-center">
        <h1 className="font-display text-4xl mb-4">Order Not Found</h1>
        <Link to="/shop">
          <Button className="btn-primary">
            Continue Shopping
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="paper-texture min-h-screen py-24">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-12">
          <CheckCircle className="w-20 h-20 text-sage mx-auto mb-6" />
          <h1 className="font-display text-5xl mb-4" data-testid="confirmation-title">
            Thank You for Your Order!
          </h1>
          <p className="text-lg text-[#5C5855]">
            Your order has been received and is being processed.
          </p>
        </div>

        {/* Order Details */}
        <div className="bg-white border border-alt-bg p-8 mb-8 soft-shadow">
          <div className="flex items-center justify-between mb-6 pb-6 border-b border-alt-bg">
            <div>
              <h2 className="font-display text-2xl mb-2">Order #{order.id.slice(0, 8)}</h2>
              <p className="text-sm text-[#8C8885]">
                Confirmation sent to {order.customerEmail}
              </p>
            </div>
            <Package className="w-12 h-12 text-cape-red" />
          </div>

          {/* Order Items */}
          <div className="mb-6">
            <h3 className="font-semibold uppercase tracking-wider text-sm mb-4">Order Items</h3>
            <div className="space-y-3">
              {order.items.map((item) => (
                <div key={item.sku} className="flex justify-between items-center">
                  <div>
                    <p className="font-medium">{item.name}</p>
                    <p className="text-sm text-[#8C8885]">Quantity: {item.quantity}</p>
                  </div>
                  <span className="font-semibold">
                    ${(item.price * item.quantity).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Shipping Address */}
          <div className="mb-6 pb-6 border-b border-alt-bg">
            <h3 className="font-semibold uppercase tracking-wider text-sm mb-2">Shipping Address</h3>
            <p className="text-[#2D2A26]">{order.customerName}</p>
            <p className="text-[#5C5855]">{order.shippingAddress}</p>
          </div>

          {/* Order Summary */}
          <div className="space-y-3">
            <div className="flex justify-between text-[#2D2A26]">
              <span>Subtotal</span>
              <span>${order.subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-[#2D2A26]">
              <span>Shipping</span>
              <span>{order.shipping === 0 ? 'FREE' : `$${order.shipping.toFixed(2)}`}</span>
            </div>
            <div className="flex justify-between text-[#2D2A26]">
              <span>Tax</span>
              <span>${order.tax.toFixed(2)}</span>
            </div>
            <div className="border-t border-alt-bg pt-3 flex justify-between font-bold text-lg">
              <span>Total</span>
              <span className="price-tag text-2xl">${order.total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-alt-bg p-8 mb-8">
          <h3 className="font-display text-2xl mb-4">What's Next?</h3>
          <ul className="space-y-3 text-[#2D2A26]">
            <li className="flex items-start">
              <span className="text-cape-red mr-3">•</span>
              <span>You'll receive an email confirmation at {order.customerEmail}</span>
            </li>
            <li className="flex items-start">
              <span className="text-cape-red mr-3">•</span>
              <span>We'll send you shipping updates as your order is processed</span>
            </li>
            <li className="flex items-start">
              <span className="text-cape-red mr-3">•</span>
              <span>Your preserves will arrive fresh and ready to enjoy!</span>
            </li>
          </ul>
        </div>

        {/* CTA Buttons */}
        <div className="text-center space-x-4">
          <Link to="/shop">
            <Button className="btn-primary" data-testid="continue-shopping-confirmation">
              Continue Shopping
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmationPage;
